import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTwoFactorAction } from "@/shared/hooks/useTwoFactorAction";
import { readApiCode, readApiStatus } from "@/shared/utils/apiError";

import { WalletTopUpsService } from "../services/walletTopUps.service";
import type { CreateWalletTopUpPayload, WalletTopUpCreated } from "../types/walletTopUps.types";
import { buildTopUpPayload } from "../utils/walletTopUp";
import {
  isSameTopUp,
  mayHaveCredited,
  reconcileTopUpAtSend,
  settleWalletTopUpLookup,
  unresolvedWalletTopUps,
  type WalletTopUpAttempt,
  type WalletTopUpLookup,
  withWalletTopUpLock,
} from "../utils/walletTopUpAttempt";

export type WalletTopUpInput = Omit<CreateWalletTopUpPayload, "idempotency_key">;

export type WalletTopUpCheck = { kind: "placed"; topUp: WalletTopUpCreated } | { kind: WalletTopUpLookup };

/** Refused client-side: an earlier top-up to the organization is still unresolved and this one differs. */
export class UnresolvedWalletTopUpError extends Error {
  constructor() {
    super("An earlier top-up to this organization is still unresolved");
    this.name = "UnresolvedWalletTopUpError";
  }
}

/**
 * The POST behind the TOTP, safe against a lost answer.
 *
 * The idempotency key follows the top-up, not the form: the same body sent again reuses the last key, and a
 * success or a different body mints a new one. The attempt (key and body) is also stored per organization
 * BEFORE the request leaves and removed only once its outcome is known — a success, or a refusal that cannot
 * have credited. So closing the dialog, a reload or another tab never loses the key: while an attempt is
 * unresolved, a retry of the same top-up reuses its key (the backend replays it instead of crediting twice)
 * and any other top-up to that organization waits for `check`, which asks the backend what became of it.
 *
 * Two tabs: both may open the challenge while nothing is unresolved, each with its own key. So the store is
 * read again when the code is sent, under a lock every tab of this browser shares, and the request leaves
 * holding it: the second tab waits for the first answer (or its loss), then sends the same top-up with the
 * first key or refuses a different one. A tab settles only the attempt it sent, never one another tab stored
 * since.
 */
export function useCreateWalletTopUp(organizationId: string | null) {
  // The answer waits for the receipt PDF and its emails, seconds after the code was checked: the dialog shows
  // its own progress instead of leaving the challenge on "Verifying…".
  const twoFactor = useTwoFactorAction<WalletTopUpCreated>({ closeOnSend: true });
  // The last body sent from this dialog with its key; null once it is known to have credited.
  const lastRef = useRef<CreateWalletTopUpPayload | null>(null);
  // Bumped on every write to the store, so the attempt read below follows it.
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((value) => value + 1), []);

  // Another tab's write shows here at once: its unresolved top-up blocks this one too.
  useEffect(() => unresolvedWalletTopUps.subscribe(bump), [bump]);

  const unresolved: WalletTopUpAttempt | null = useMemo(
    () => (organizationId ? unresolvedWalletTopUps.get(organizationId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `version` is the store's change signal
    [organizationId, version],
  );

  const { start } = twoFactor;
  const submit = useCallback(
    async (input: WalletTopUpInput, currency: string | null): Promise<WalletTopUpCreated | null> => {
      const organization = input.organization_id;
      const pending = unresolvedWalletTopUps.get(organization);
      if (pending && !isSameTopUp(pending.payload, input)) throw new UnresolvedWalletTopUpError();

      const last = lastRef.current;
      const key =
        pending?.payload.idempotency_key ??
        (last && isSameTopUp(last, input) ? last.idempotency_key : crypto.randomUUID());
      let payload = buildTopUpPayload({ ...input, idempotency_key: key });
      lastRef.current = payload;
      // The attempt sent was unresolved before this submit (here or in another tab): only a success or `check`
      // settles it, since a refusal of this retry says nothing about the request whose answer was lost.
      let inherited = !!pending;
      let sent = false;
      try {
        const created = await start((code) =>
          withWalletTopUpLock(organization, () => {
            const decision = reconcileTopUpAtSend(unresolvedWalletTopUps.get(organization), payload);
            if (!decision) throw new UnresolvedWalletTopUpError();
            if (decision.adopted) {
              payload = decision.payload;
              lastRef.current = payload;
              inherited = true;
            }
            // Before the request leaves: a lost answer must find the key here, whatever happens to the dialog.
            unresolvedWalletTopUps.set(organization, { payload, currency });
            sent = true;
            return WalletTopUpsService.create(payload, code);
          }),
        );
        if (created) {
          unresolvedWalletTopUps.clear(organization, payload.idempotency_key);
          lastRef.current = null;
        } else if (sent && !inherited) {
          // Cancelled at the prompt: every code tried was refused before anything ran.
          unresolvedWalletTopUps.clear(organization, payload.idempotency_key);
        }
        return created;
      } catch (error: unknown) {
        if (sent && !inherited && !mayHaveCredited(readApiStatus(error))) {
          unresolvedWalletTopUps.clear(organization, payload.idempotency_key);
        }
        throw error;
      } finally {
        bump();
      }
    },
    [start, bump],
  );

  /** Asks the backend what became of the unresolved attempt. No 2FA: it only reads. */
  const check = useCallback(async (): Promise<WalletTopUpCheck> => {
    if (!organizationId) return { kind: "unknown" };
    const pending = unresolvedWalletTopUps.get(organizationId);
    if (!pending) return { kind: "not_placed" };
    try {
      const topUp = await WalletTopUpsService.findByKey(organizationId, pending.payload.idempotency_key);
      unresolvedWalletTopUps.clear(organizationId, pending.payload.idempotency_key);
      lastRef.current = null;
      // Found by its key: it was credited once, and this read credited nothing.
      return { kind: "placed", topUp: { ...topUp, replayed: true, emails: null } };
    } catch (error: unknown) {
      const outcome = settleWalletTopUpLookup(readApiStatus(error), readApiCode(error));
      if (outcome === "not_placed") {
        unresolvedWalletTopUps.clear(organizationId, pending.payload.idempotency_key);
        // Typed again, the same top-up keeps this key: even a request still on the wire could only credit once.
        lastRef.current = pending.payload;
      }
      return { kind: outcome };
    } finally {
      bump();
    }
  }, [organizationId, bump]);

  return { submit, check, unresolved, twoFactor };
}
